import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Modal,
  Row,
  Col,
  Form,
  Button,
  ListGroup,
} from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Selector, { components as rsComponents } from 'react-select';
import { FaPlus, FaTrash, FaTimes } from 'react-icons/fa';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { listarOutorgantesDisponiveis } from '~/Services/Service';
import ModalCadastroExterno from './ModalCadastroExterno';
import { inputStyles } from '~/Styles/inputStyles';
import { formatDocumento } from './helpers';

const CheckboxOption = (props) => (
  <rsComponents.Option {...props}>
    <input
      type="checkbox"
      checked={props.isSelected}
      onChange={() => null}
      style={{ marginRight: 8, verticalAlign: 'middle' }}
    />
    <span style={{ verticalAlign: 'middle' }}>{props.label}</span>
  </rsComponents.Option>
);

const MultiValueSummary = ({ children, ...props }) => {
  const selected = props.getValue();
  const inputChild = React.Children.toArray(children).find(
    (c) => c && c.props && c.props.type === 'text'
  ) || children[children.length - 1];
  if (selected.length === 0) {
    return <rsComponents.ValueContainer {...props}>{children}</rsComponents.ValueContainer>;
  }
  const resumo = selected.length <= 2
    ? selected.map((s) => s.label).join(', ')
    : selected.slice(0, 2).map((s) => s.label).join(', ') + ' +' + (selected.length - 2);
  return (
    <rsComponents.ValueContainer {...props}>
      <span style={{ fontSize: '0.875rem', color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{resumo}</span>
      {inputChild}
    </rsComponents.ValueContainer>
  );
};

const errorSelectStyles = {
  ...customStyles,
  control: (base) => ({
    ...customStyles.control(base),
    borderColor: '#dc3545',
    '&:hover': { borderColor: '#dc3545' },
  }),
};

const EXTENSOES_PERMITIDAS = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.zip,.rar';

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
};

const GRUPO_OPTIONS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((l) => ({
  value: l,
  label: l,
}));

const validationSchema = Yup.object().shape({
  codigo_tipo_instrumento: Yup.number().required('Obrigatório').min(1, 'Obrigatório'),
  codigo_tipo_poder: Yup.number().required('Obrigatório').min(1, 'Obrigatório'),
  codigo_tipo_substabelecimento: Yup.number().required('Obrigatório').min(1, 'Obrigatório'),
  data_lavratura: Yup.string().required('Obrigatório'),
  tabeliao_nome: Yup.string().when('codigo_tipo_instrumento', {
    is: 1,
    then: Yup.string().required('Obrigatório para instrumento público'),
  }),
  tabeliao_detalhes: Yup.string().when('codigo_tipo_instrumento', {
    is: 1,
    then: Yup.string().required('Obrigatório para instrumento público'),
  }),
});

const emptyValues = {
  codigo_tipo_instrumento: '',
  codigo_tipo_poder: '',
  codigo_tipo_substabelecimento: '',
  data_lavratura: '',
  data_vencimento: '',
  indeterminada: false,
  tabeliao_nome: '',
  tabeliao_detalhes: '',
  codigo_enotoriado: '',
  estados_validade: '',
  observacao: '',
};

const buildInitialValues = (edicao) => {
  if (!edicao) return emptyValues;
  const proc = edicao.procuracao;
  return {
    codigo_tipo_instrumento: proc.codigo_tipo_instrumento,
    codigo_tipo_poder: proc.codigo_tipo_poder,
    codigo_tipo_substabelecimento: proc.codigo_tipo_substabelecimento,
    data_lavratura: proc.data_lavratura ? proc.data_lavratura.substring(0, 10) : '',
    data_vencimento: proc.data_vencimento ? proc.data_vencimento.substring(0, 10) : '',
    indeterminada: !proc.data_vencimento,
    tabeliao_nome: proc.tabeliao_nome || '',
    tabeliao_detalhes: proc.tabeliao_detalhes || '',
    codigo_enotoriado: proc.codigo_enotoriado || '',
    estados_validade: proc.estados_validade || '',
    observacao: proc.observacao || '',
  };
};

export default function ProcuracaoForm({
  show,
  onSalvar,
  onCancelar,
  procuracaoEdicao,
  optionsInstrumento,
  optionsTipoPoder,
  optionsSubstabelecimento,
  optionsEstados,
}) {
  const modoEdicao = procuracaoEdicao != null;
  const codigoEdicao = modoEdicao ? procuracaoEdicao.procuracao.codigo : null;

  const [tipoOutorgante, setTipoOutorgante] = useState('PJ');
  const [tipoOutorgado, setTipoOutorgado] = useState('PJ');
  const [disponiveisOutorgantes, setDisponiveisOutorgantes] = useState([]);
  const [disponiveisOutorgados, setDisponiveisOutorgados] = useState([]);
  const [loadingOutorgantes, setLoadingOutorgantes] = useState(false);
  const [loadingOutorgados, setLoadingOutorgados] = useState(false);
  const [showCadastroExterno, setShowCadastroExterno] = useState(false);
  const [menuEstadosAberto, setMenuEstadosAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [errosListas, setErrosListas] = useState({});
  const [anexos, setAnexos] = useState([]);
  const fileInputRef = useRef(null);

  // Listas de itens (existentes + novos)
  const [outorgantes, setOutorgantes] = useState([]);
  const [outorgados, setOutorgados] = useState([]);
  const [poderes, setPoderes] = useState([]);

  // IDs marcados para remoção (apenas edição)
  const [outorgantesRemover, setOutorgantesRemover] = useState([]);
  const [outorgadosRemover, setOutorgadosRemover] = useState([]);
  const [poderesRemover, setPoderesRemover] = useState([]);

  useEffect(() => {
    if (!show) return;
    setTipoOutorgante('PJ');
    setTipoOutorgado('PJ');

    if (procuracaoEdicao) {
      setOutorgantes((procuracaoEdicao.outorgantes || []).map((o) => ({ ...o, _existente: true })));
      setOutorgados((procuracaoEdicao.outorgados || []).map((o) => ({ ...o, _existente: true })));
      setPoderes((procuracaoEdicao.poderes || []).map((p) => ({ ...p, _existente: true })));
    } else {
      setOutorgantes([]);
      setOutorgados([]);
      setPoderes([]);
    }
    setOutorgantesRemover([]);
    setOutorgadosRemover([]);
    setPoderesRemover([]);
    setAnexos([]);
  }, [show, procuracaoEdicao]);

  useEffect(() => {
    if (!show) return;
    setLoadingOutorgantes(true);
    listarOutorgantesDisponiveis(tipoOutorgante)
      .then((data) => { setDisponiveisOutorgantes(Array.isArray(data) ? data : []); })
      .catch(() => { setDisponiveisOutorgantes([]); })
      .finally(() => { setLoadingOutorgantes(false); });
  }, [show, tipoOutorgante]);

  useEffect(() => {
    if (!show) return;
    setLoadingOutorgados(true);
    listarOutorgantesDisponiveis(tipoOutorgado)
      .then((data) => { setDisponiveisOutorgados(Array.isArray(data) ? data : []); })
      .catch(() => { setDisponiveisOutorgados([]); })
      .finally(() => { setLoadingOutorgados(false); });
  }, [show, tipoOutorgado]);

  const recarregarDisponiveis = () => {
    setLoadingOutorgantes(true);
    listarOutorgantesDisponiveis(tipoOutorgante)
      .then((data) => { setDisponiveisOutorgantes(Array.isArray(data) ? data : []); })
      .catch(() => { setDisponiveisOutorgantes([]); })
      .finally(() => { setLoadingOutorgantes(false); });
    setLoadingOutorgados(true);
    listarOutorgantesDisponiveis(tipoOutorgado)
      .then((data) => { setDisponiveisOutorgados(Array.isArray(data) ? data : []); })
      .catch(() => { setDisponiveisOutorgados([]); })
      .finally(() => { setLoadingOutorgados(false); });
  };

  const toOption = (d) => ({
    value: d,
    label: d.nome + (d.documento ? ' (' + d.documento + ')' : ''),
  });

  const normalizeDoc = (v) => String(v || '').replace(/\D/g, '');

  const filtrarJaAdicionados = (options, lista) =>
    options.filter((opt) => {
      const d = opt.value;
      const docD = normalizeDoc(d.documento);
      return !lista.some((o) => {
        if (o._removido) return false;
        if (d.codigo_razao && o.codigo_razao === d.codigo_razao) return true;
        if (d.codigo_externo && o.codigo_externo === d.codigo_externo) return true;
        if (docD && normalizeDoc(o.documento) === docD) return true;
        return false;
      });
    });

  const jaAdicionados = useMemo(
    () => [...outorgantes, ...outorgados],
    [outorgantes, outorgados]
  );

  const optionsOutorgantesDisponiveis = useMemo(
    () => filtrarJaAdicionados(disponiveisOutorgantes.map(toOption), jaAdicionados),
    [disponiveisOutorgantes, jaAdicionados]
  );
  const optionsOutorgadosDisponiveis = useMemo(
    () => filtrarJaAdicionados(disponiveisOutorgados.map(toOption), jaAdicionados),
    [disponiveisOutorgados, jaAdicionados]
  );

  const documentosConhecidos = useMemo(() => {
    const lista = [];
    jaAdicionados.forEach((o) => {
      if (!o._removido && o.documento) {
        lista.push({ documento: o.documento, nome: o.nome, origem: 'GRID' });
      }
    });
    [...disponiveisOutorgantes, ...disponiveisOutorgados].forEach((d) => {
      if (d.documento) {
        lista.push({ documento: d.documento, nome: d.nome, origem: d.origem });
      }
    });
    return lista;
  }, [jaAdicionados, disponiveisOutorgantes, disponiveisOutorgados]);

  const handleAdicionarOutorgante = (option) => {
    if (!option) return;
    const item = option.value;
    const docItem = normalizeDoc(item.documento);
    const jaTem = jaAdicionados.some((o) =>
      !o._removido &&
      ((item.codigo_razao && o.codigo_razao === item.codigo_razao) ||
      (item.codigo_externo && o.codigo_externo === item.codigo_externo) ||
      (docItem && normalizeDoc(o.documento) === docItem))
    );
    if (jaTem) return;
    setOutorgantes([...outorgantes, {
      origem: item.origem,
      codigo_razao: item.codigo_razao || null,
      codigo_externo: item.codigo_externo || null,
      cpf: item.cpf || null,
      rg: item.rg || null,
      nome: item.nome,
      documento: item.documento,
    }]);
  };

  const handleRemoverOutorgante = (idx) => {
    const item = outorgantes[idx];
    if (item._existente && item.codigo) {
      setOutorgantesRemover([...outorgantesRemover, item.codigo]);
    }
    setOutorgantes(outorgantes.filter((_, i) => i !== idx));
  };

  const handleAdicionarOutorgado = (option) => {
    if (!option) return;
    const item = option.value;
    const docItem = normalizeDoc(item.documento);
    const jaTem = jaAdicionados.some((o) =>
      !o._removido &&
      ((item.codigo_razao && o.codigo_razao === item.codigo_razao) ||
      (item.codigo_externo && o.codigo_externo === item.codigo_externo) ||
      (docItem && normalizeDoc(o.documento) === docItem))
    );
    if (jaTem) return;
    setOutorgados([...outorgados, {
      origem: item.origem,
      codigo_razao: item.codigo_razao || null,
      codigo_externo: item.codigo_externo || null,
      cpf: item.cpf || null,
      rg: item.rg || null,
      grupo: '',
      nome: item.nome,
      documento: item.documento,
    }]);
  };

  const handleRemoverOutorgado = (idx) => {
    const item = outorgados[idx];
    if (item._existente && item.codigo) {
      setOutorgadosRemover([...outorgadosRemover, item.codigo]);
    }
    setOutorgados(outorgados.filter((_, i) => i !== idx));
  };

  const handleAtualizarOutorgado = (idx, campo, valor) => {
    setOutorgados(outorgados.map((item, i) =>
      i === idx ? { ...item, [campo]: valor } : item
    ));
  };

  const handleAdicionarPoder = () => {
    const proximoNumero = poderes.length > 0
      ? Math.max(...poderes.map((p) => p.numero || 0)) + 1
      : 1;
    setPoderes([...poderes, {
      numero: proximoNumero,
      descricao: '',
      grupo: '',
      restricao: false,
    }]);
  };

  const handleRemoverPoder = (idx) => {
    const item = poderes[idx];
    if (item._existente && item.codigo) {
      setPoderesRemover([...poderesRemover, item.codigo]);
    }
    setPoderes(poderes.filter((_, i) => i !== idx));
  };

  const handleAtualizarPoder = (idx, campo, valor) => {
    setPoderes(poderes.map((item, i) =>
      i === idx ? { ...item, [campo]: valor } : item
    ));
  };

  const handleSubmit = async (values) => {
    // Validar listas mínimas e regras de negócio
    const erros = {};
    if (outorgantes.length === 0) erros.outorgantes = 'Mínimo 1 outorgante';
    if (outorgados.length === 0) erros.outorgados = 'Mínimo 1 outorgado';
    if (poderes.length === 0) {
      erros.poderes = 'Mínimo 1 poder';
    } else {
      const poderesNovos = poderes.filter((p) => !p._existente);
      const poderInvalido = poderesNovos.find((p) => !p.descricao || !p.descricao.trim());
      if (poderInvalido) erros.poderes = 'Descrição do poder é obrigatória';
      const poderSemNumero = poderesNovos.find((p) => !p.numero || p.numero <= 0);
      if (poderSemNumero) erros.poderes = 'Número do poder deve ser maior que zero';
      const numeros = poderes.map((p) => p.numero);
      const numeroDuplicado = numeros.find((n, i) => n && numeros.indexOf(n) !== i);
      if (numeroDuplicado) erros.poderes = 'Número do poder duplicado: ' + numeroDuplicado;
    }
    if (values.data_vencimento && values.data_lavratura && !values.indeterminada) {
      if (values.data_vencimento <= values.data_lavratura) {
        erros.data_vencimento = 'Data de vencimento deve ser posterior à data de lavratura';
      }
    }
    if (Object.keys(erros).length > 0) {
      setErrosListas(erros);
      return;
    }
    setErrosListas({});
    setSalvando(true);
    try {
    if (modoEdicao) {
      // Payload diff para edição
      const novosOutorgantes = outorgantes.filter((o) => !o._existente);
      const novosOutorgados = outorgados.filter((o) => !o._existente);
      const novosPoderes = poderes.filter((p) => !p._existente);

      // Existentes com grupo alterado: UPDATE dedicado no backend
      const existentesOriginais = (procuracaoEdicao.outorgados || []);
      const gruposAlterados = outorgados
        .filter((o) =>
          o._existente && o.codigo &&
          existentesOriginais.some((orig) => orig.codigo === o.codigo && (orig.grupo || '') !== (o.grupo || ''))
        )
        .map((o) => ({ codigo: o.codigo, grupo: o.grupo || null }));

      const payload = {
        codigo_tipo_instrumento: values.codigo_tipo_instrumento,
        codigo_tipo_poder: values.codigo_tipo_poder,
        codigo_tipo_substabelecimento: values.codigo_tipo_substabelecimento,
        data_lavratura: values.data_lavratura,
        data_vencimento: values.indeterminada ? null : (values.data_vencimento || null),
        tabeliao_nome: values.tabeliao_nome || null,
        tabeliao_detalhes: values.tabeliao_detalhes || null,
        codigo_enotoriado: values.codigo_enotoriado || null,
        estados_validade: values.estados_validade || null,
        observacao: values.observacao || null,
        outorgantes_adicionar: novosOutorgantes.length > 0
          ? novosOutorgantes.map((o) => ({
              origem: o.origem,
              codigo_razao: o.codigo_razao || null,
              codigo_externo: o.codigo_externo || null,
              cpf: o.cpf || null,
              nome: o.nome || null,
              rg: o.rg || null,
            }))
          : null,
        outorgantes_remover: outorgantesRemover.length > 0 ? outorgantesRemover : null,
        outorgados_adicionar: novosOutorgados.length > 0
          ? novosOutorgados.map((o) => ({
              origem: o.origem,
              codigo_razao: o.codigo_razao || null,
              codigo_externo: o.codigo_externo || null,
              cpf: o.cpf || null,
              nome: o.nome || null,
              rg: o.rg || null,
              grupo: o.grupo || null,
            }))
          : null,
        outorgados_remover: outorgadosRemover.length > 0 ? outorgadosRemover : null,
        outorgados_atualizar_grupo: gruposAlterados.length > 0 ? gruposAlterados : null,
        poderes_adicionar: novosPoderes.length > 0
          ? novosPoderes.map((p) => ({ numero: p.numero, descricao: p.descricao, grupo: p.grupo || null, restricao: p.restricao, codigo_poder_origem: null }))
          : null,
        poderes_remover: poderesRemover.length > 0 ? poderesRemover : null,
      };
      await onSalvar(payload, codigoEdicao, anexos);
    } else {
      // Payload criação
      const payload = {
        codigo_tipo_instrumento: values.codigo_tipo_instrumento,
        codigo_tipo_poder: values.codigo_tipo_poder,
        codigo_tipo_substabelecimento: values.codigo_tipo_substabelecimento,
        data_lavratura: values.data_lavratura,
        data_vencimento: values.indeterminada ? null : (values.data_vencimento || null),
        tabeliao_nome: values.tabeliao_nome || null,
        tabeliao_detalhes: values.tabeliao_detalhes || null,
        codigo_enotoriado: values.codigo_enotoriado || null,
        estados_validade: values.estados_validade || null,
        observacao: values.observacao || null,
        outorgantes: outorgantes.map((o) => ({
          origem: o.origem,
          codigo_razao: o.codigo_razao || null,
          codigo_externo: o.codigo_externo || null,
          cpf: o.cpf || null,
          nome: o.nome || null,
          rg: o.rg || null,
        })),
        outorgados: outorgados.map((o) => ({
          origem: o.origem,
          codigo_razao: o.codigo_razao || null,
          codigo_externo: o.codigo_externo || null,
          cpf: o.cpf || null,
          nome: o.nome || null,
          rg: o.rg || null,
          grupo: o.grupo || null,
        })),
        poderes: poderes.map((p) => ({ numero: p.numero, descricao: p.descricao, grupo: p.grupo || null, restricao: p.restricao, codigo_poder_origem: null })),
      };
      await onSalvar(payload, null, anexos);
    }
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
    <Modal show={show} onHide={onCancelar} size="xl" centered backdrop="static" scrollable>
      {showCadastroExterno && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1,
          borderRadius: '.3rem',
        }} />
      )}
      <Formik
        initialValues={buildInitialValues(procuracaoEdicao)}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit: formSubmit,
          setFieldValue,
        }) => (
          <Form onSubmit={formSubmit}>
            <Modal.Header closeButton>
              <Modal.Title className="h6">
                {modoEdicao ? 'Editar Procuração #' + codigoEdicao : 'Nova Procuração'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {/* === Campos principais === */}
              <Row>
                <Col md={3} className="mb-2">
                  <Form.Label className="small mb-0">Instrumento <span className="text-danger">*</span></Form.Label>
                  <Selector
                    styles={touched.codigo_tipo_instrumento && errors.codigo_tipo_instrumento ? errorSelectStyles : customStyles}
                    components={{ DropdownIndicator }}
                    placeholder="Selecione"
                    options={optionsInstrumento}
                    onChange={(opt) => { setFieldValue('codigo_tipo_instrumento', opt ? opt.value : ''); }}
                    value={
                      values.codigo_tipo_instrumento
                        ? optionsInstrumento.find((o) => o.value === values.codigo_tipo_instrumento)
                        : null
                    }
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    noOptionsMessage={() => 'Nenhuma opção'}
                  />
                  {touched.codigo_tipo_instrumento && errors.codigo_tipo_instrumento && (
                    <small className="text-danger">{errors.codigo_tipo_instrumento}</small>
                  )}
                </Col>
                <Col md={3} className="mb-2">
                  <Form.Label className="small mb-0">Tipo Poder <span className="text-danger">*</span></Form.Label>
                  <Selector
                    styles={touched.codigo_tipo_poder && errors.codigo_tipo_poder ? errorSelectStyles : customStyles}
                    components={{ DropdownIndicator }}
                    placeholder="Selecione"
                    options={optionsTipoPoder}
                    onChange={(opt) => { setFieldValue('codigo_tipo_poder', opt ? opt.value : ''); }}
                    value={
                      values.codigo_tipo_poder
                        ? optionsTipoPoder.find((o) => o.value === values.codigo_tipo_poder)
                        : null
                    }
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    noOptionsMessage={() => 'Nenhuma opção'}
                  />
                  {touched.codigo_tipo_poder && errors.codigo_tipo_poder && (
                    <small className="text-danger">{errors.codigo_tipo_poder}</small>
                  )}
                </Col>
                <Col md={3} className="mb-2">
                  <Form.Label className="small mb-0">Substabelecimento <span className="text-danger">*</span></Form.Label>
                  <Selector
                    styles={touched.codigo_tipo_substabelecimento && errors.codigo_tipo_substabelecimento ? errorSelectStyles : customStyles}
                    components={{ DropdownIndicator }}
                    options={optionsSubstabelecimento}
                    onChange={(opt) => { setFieldValue('codigo_tipo_substabelecimento', opt ? opt.value : ''); }}
                    value={
                      values.codigo_tipo_substabelecimento
                        ? optionsSubstabelecimento.find((o) => o.value === values.codigo_tipo_substabelecimento)
                        : null
                    }
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    noOptionsMessage={() => 'Nenhuma opção'}
                  />
                  {touched.codigo_tipo_substabelecimento && errors.codigo_tipo_substabelecimento && (
                    <small className="text-danger">{errors.codigo_tipo_substabelecimento}</small>
                  )}
                </Col>
                <Col md={3} className="mb-2">
                  <Form.Label className="small mb-0">Data Lavratura <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    name="data_lavratura"
                    value={values.data_lavratura}
                    onChange={handleChange}
                    style={inputStyles}
                    isInvalid={touched.data_lavratura && !!errors.data_lavratura}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.data_lavratura}
                  </Form.Control.Feedback>
                </Col>
              </Row>

              <Row>
                <Col md={3} className="mb-2">
                  <Form.Label className="small mb-0">Data Vencimento</Form.Label>
                  <Form.Control
                    type="date"
                    name="data_vencimento"
                    value={values.indeterminada ? '' : values.data_vencimento}
                    onChange={handleChange}
                    style={{
                      ...inputStyles,
                      ...(errosListas.data_vencimento ? { borderColor: '#dc3545' } : {}),
                    }}
                    disabled={values.indeterminada}
                  />
                  {errosListas.data_vencimento && (
                    <small className="text-danger">{errosListas.data_vencimento}</small>
                  )}
                  <Form.Check
                    type="checkbox"
                    id="indeterminada-check"
                    label="Indeterminada"
                    className="mt-1"
                    checked={values.indeterminada}
                    onChange={(e) => {
                      setFieldValue('indeterminada', e.target.checked);
                      if (e.target.checked) setFieldValue('data_vencimento', '');
                    }}
                  />
                </Col>
                <Col md={3} className="mb-2">
                  <Form.Label className="small mb-0">Estados</Form.Label>
                  <Selector
                    styles={{
                      ...customStyles,
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused ? '#f0f0f0' : '#fff',
                        color: '#333',
                        cursor: 'pointer',
                        padding: '6px 10px',
                      }),
                    }}
                    components={{ DropdownIndicator, Option: CheckboxOption, ValueContainer: MultiValueSummary }}
                    isMulti
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    placeholder="Selecione..."
                    options={optionsEstados}
                    value={
                      values.estados_validade
                        ? values.estados_validade
                            .split(',')
                            .map((v) => v.trim())
                            .filter(Boolean)
                            .map((v) => (optionsEstados || []).find((o) => o.value === v))
                            .filter(Boolean)
                        : []
                    }
                    onChange={(selected) => {
                      setFieldValue(
                        'estados_validade',
                        selected ? selected.map((s) => s.value).join(',') : ''
                      );
                    }}
                    menuIsOpen={menuEstadosAberto}
                    onMenuOpen={() => setMenuEstadosAberto(true)}
                    onMenuClose={() => setMenuEstadosAberto(false)}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    noOptionsMessage={() => 'Nenhuma opção'}
                    isClearable
                  />
                </Col>
                <Col md={3} className="mb-2">
                  <Form.Label className="small mb-0">Cod. e-Notariado</Form.Label>
                  <Form.Control
                    name="codigo_enotoriado"
                    value={values.codigo_enotoriado}
                    onChange={handleChange}
                    style={inputStyles}
                  />
                </Col>
                <Col md={3} className="mb-2">
                  <Form.Label className="small mb-0">Observação</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={1}
                    name="observacao"
                    value={values.observacao}
                    onChange={handleChange}
                  />
                </Col>
              </Row>

              {values.codigo_tipo_instrumento === 1 && (
                <Row>
                  <Col md={6} className="mb-2">
                    <Form.Label className="small mb-0">Tabelião Nome <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      name="tabeliao_nome"
                      value={values.tabeliao_nome}
                      onChange={handleChange}
                      style={inputStyles}
                      isInvalid={touched.tabeliao_nome && !!errors.tabeliao_nome}
                    />
                    <Form.Control.Feedback type="invalid">{errors.tabeliao_nome}</Form.Control.Feedback>
                  </Col>
                  <Col md={6} className="mb-2">
                    <Form.Label className="small mb-0">Tabelião Detalhes <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      name="tabeliao_detalhes"
                      placeholder="Livro, folhas, etc."
                      value={values.tabeliao_detalhes}
                      onChange={handleChange}
                      style={inputStyles}
                      isInvalid={touched.tabeliao_detalhes && !!errors.tabeliao_detalhes}
                    />
                    <Form.Control.Feedback type="invalid">{errors.tabeliao_detalhes}</Form.Control.Feedback>
                  </Col>
                </Row>
              )}

              <hr className="my-2" />

              {/* === Outorgantes === */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Form.Label className="small mb-0 font-weight-bold">
                    Outorgantes ({outorgantes.length})
                  </Form.Label>
                  <div>
                    <Form.Check
                      type="radio"
                      inline
                      id="tipo-outorgante-pj"
                      name="tipoOutorgante"
                      label="PJ"
                      checked={tipoOutorgante === 'PJ'}
                      onChange={() => setTipoOutorgante('PJ')}
                    />
                    <Form.Check
                      type="radio"
                      inline
                      id="tipo-outorgante-pf"
                      name="tipoOutorgante"
                      label="PF"
                      checked={tipoOutorgante === 'PF'}
                      onChange={() => setTipoOutorgante('PF')}
                    />
                  </div>
                </div>
                {errosListas.outorgantes && (
                  <small className="text-danger d-block mb-1">{errosListas.outorgantes}</small>
                )}
                <div className="d-flex">
                  <div style={{ flex: 1 }}>
                    <Selector
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      placeholder={loadingOutorgantes ? 'Carregando...' : 'Selecionar outorgante...'}
                      options={optionsOutorgantesDisponiveis}
                      isDisabled={loadingOutorgantes}
                      isClearable
                      value={null}
                      onChange={handleAdicionarOutorgante}
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                      noOptionsMessage={() => 'Nenhum disponível'}
                    />
                  </div>
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="ml-2"
                    title="Cadastrar novo externo"
                    onClick={() => { setMenuEstadosAberto(false); setShowCadastroExterno(true); }}
                    style={{ height: 38 }}
                  >
                    <FaPlus />
                  </Button>
                </div>
                {outorgantes.length > 0 && (
                  <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
                    <table className="table table-sm table-bordered mb-0 mt-1" style={{ fontSize: '13px' }}>
                      <thead style={{ backgroundColor: '#0747a6', color: '#fff', position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr>
                          <th>Nome</th>
                          <th>Documento</th>
                          <th style={{ width: '50px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {outorgantes.map((o, idx) => (
                          <tr key={`outorgante-${o.codigo || o.codigo_razao || o.codigo_externo}-${idx}`}>
                            <td>{o.nome}</td>
                            <td style={{ fontFamily: 'monospace' }}>{formatDocumento(o.documento)}</td>
                            <td className="text-center">
                              <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => { handleRemoverOutorgante(idx); }}>
                                <FaTrash />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* === Outorgados === */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Form.Label className="small mb-0 font-weight-bold">
                    Outorgados ({outorgados.length})
                  </Form.Label>
                  <div>
                    <Form.Check
                      type="radio"
                      inline
                      id="tipo-outorgado-pj"
                      name="tipoOutorgado"
                      label="PJ"
                      checked={tipoOutorgado === 'PJ'}
                      onChange={() => setTipoOutorgado('PJ')}
                    />
                    <Form.Check
                      type="radio"
                      inline
                      id="tipo-outorgado-pf"
                      name="tipoOutorgado"
                      label="PF"
                      checked={tipoOutorgado === 'PF'}
                      onChange={() => setTipoOutorgado('PF')}
                    />
                  </div>
                </div>
                {errosListas.outorgados && (
                  <small className="text-danger d-block mb-1">{errosListas.outorgados}</small>
                )}
                <div className="d-flex">
                  <div style={{ flex: 1 }}>
                    <Selector
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      placeholder={loadingOutorgados ? 'Carregando...' : 'Selecionar outorgado...'}
                      options={optionsOutorgadosDisponiveis}
                      isDisabled={loadingOutorgados}
                      isClearable
                      value={null}
                      onChange={handleAdicionarOutorgado}
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                      noOptionsMessage={() => 'Nenhum disponível'}
                    />
                  </div>
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="ml-2"
                    title="Cadastrar novo externo"
                    onClick={() => { setMenuEstadosAberto(false); setShowCadastroExterno(true); }}
                    style={{ height: 38 }}
                  >
                    <FaPlus />
                  </Button>
                </div>
                {outorgados.length > 0 && (
                  <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
                    <table className="table table-sm table-bordered mb-0 mt-1" style={{ fontSize: '13px' }}>
                      <thead style={{ backgroundColor: '#0747a6', color: '#fff', position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr>
                          <th>Nome</th>
                          <th>Documento</th>
                          <th>Grupo</th>
                          <th style={{ width: '50px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {outorgados.map((o, idx) => (
                          <tr key={`outorgado-${o.codigo || o.codigo_razao || o.codigo_externo}-${idx}`}>
                            <td>{o.nome}</td>
                            <td style={{ fontFamily: 'monospace' }}>{formatDocumento(o.documento)}</td>
                            <td style={{ width: '140px' }}>
                              <Selector
                                styles={customStyles}
                                components={{ DropdownIndicator }}
                                placeholder="-"
                                options={GRUPO_OPTIONS}
                                isClearable
                                value={o.grupo ? GRUPO_OPTIONS.find((g) => g.value === o.grupo) : null}
                                onChange={(opt) => { handleAtualizarOutorgado(idx, 'grupo', opt ? opt.value : ''); }}
                                menuPortalTarget={document.body}
                                menuPlacement="auto"
                              />
                            </td>
                            <td className="text-center">
                              <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => { handleRemoverOutorgado(idx); }}>
                                <FaTrash />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* === Poderes === */}
              <div className="mb-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Form.Label className="small mb-0 font-weight-bold">
                    Poderes ({poderes.length})
                  </Form.Label>
                  <Button variant="outline-primary" size="sm" onClick={handleAdicionarPoder}>
                    <FaPlus className="mr-1" /> Adicionar
                  </Button>
                </div>
                {errosListas.poderes && (
                  <small className="text-danger d-block mb-1">{errosListas.poderes}</small>
                )}
                {poderes.length > 0 && (
                  <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
                    <table className="table table-sm table-bordered mb-0" style={{ fontSize: '13px' }}>
                      <thead style={{ backgroundColor: '#0747a6', color: '#fff', position: 'sticky', top: 0, zIndex: 2 }}>
                        <tr>
                          <th style={{ width: '70px' }}>Num. <span className="text-danger">*</span></th>
                          <th>Descrição <span className="text-danger">*</span></th>
                          <th style={{ width: '100px' }}>Grupo</th>
                          <th style={{ width: '70px' }}>Restr.</th>
                          <th style={{ width: '50px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {poderes.map((p, idx) => (
                          <tr key={`poder-${p.codigo || idx}`}>
                            <td>
                              <Form.Control
                                type="number"
                                min={1}
                                value={p.numero}
                                onChange={(e) => { handleAtualizarPoder(idx, 'numero', parseInt(e.target.value, 10) || ''); }}
                                style={{ ...inputStyles, fontSize: '13px', padding: '2px 4px' }}
                                disabled={p._existente}
                              />
                            </td>
                            <td>
                              <Form.Control
                                value={p.descricao}
                                onChange={(e) => { handleAtualizarPoder(idx, 'descricao', e.target.value); }}
                                style={{ fontSize: '13px', padding: '2px 4px' }}
                                disabled={p._existente}
                              />
                            </td>
                            <td>
                              <Selector
                                styles={customStyles}
                                components={{ DropdownIndicator }}
                                placeholder="-"
                                options={GRUPO_OPTIONS}
                                isClearable
                                value={p.grupo ? GRUPO_OPTIONS.find((g) => g.value === p.grupo) : null}
                                onChange={(opt) => { handleAtualizarPoder(idx, 'grupo', opt ? opt.value : ''); }}
                                menuPortalTarget={document.body}
                                menuPlacement="auto"
                                isDisabled={p._existente}
                              />
                            </td>
                            <td className="text-center align-middle">
                              <Form.Check
                                type="switch"
                                id={'poder-restricao-' + idx}
                                checked={p.restricao}
                                onChange={(e) => { handleAtualizarPoder(idx, 'restricao', e.target.checked); }}
                                disabled={p._existente}
                              />
                            </td>
                            <td className="text-center align-middle">
                              <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => { handleRemoverPoder(idx); }}>
                                <FaTrash />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <hr className="my-2" />

              {/* === Anexos === */}
              <div className="mb-2">
                <Form.Label className="small mb-1 font-weight-bold">
                  Anexos ({anexos.length})
                </Form.Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={EXTENSOES_PERMITIDAS}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length > 0) {
                      setAnexos((prev) => [...prev, ...files]);
                    }
                    e.target.value = '';
                  }}
                />
                <div style={{
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  padding: '8px',
                  minHeight: '80px',
                  maxHeight: '160px',
                  overflowY: 'auto',
                  backgroundColor: '#f8f9fa',
                }}>
                  {anexos.length > 0 ? (
                    <ListGroup variant="flush">
                      {anexos.map((file, idx) => (
                        <ListGroup.Item
                          key={`anexo-${file.name}-${idx}`}
                          className="d-flex justify-content-between align-items-center px-2 py-1"
                          style={{ backgroundColor: 'transparent', fontSize: '0.85rem' }}
                        >
                          <span className="text-truncate mr-2" title={file.name}>
                            {file.name}
                          </span>
                          <div className="d-flex align-items-center">
                            <small className="text-muted mr-2">
                              {formatFileSize(file.size)}
                            </small>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              title="Remover"
                              onClick={() => {
                                setAnexos(anexos.filter((_, i) => i !== idx));
                              }}
                            >
                              <FaTimes size={10} />
                            </Button>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : (
                    <p className="text-muted text-center mb-0 mt-2" style={{ fontSize: '0.85rem' }}>
                      Nenhum anexo adicionado
                    </p>
                  )}
                </div>
                <div className="d-flex align-items-center mt-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => { fileInputRef.current.click(); }}
                  >
                    <FaPlus size={10} className="mr-1" />
                    Adicionar Anexo
                  </Button>
                  <small className="text-muted ml-2">
                    Permitidos: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, JPEG, PNG, GIF, ZIP, RAR
                  </small>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" size="sm" onClick={onCancelar}>
                Cancelar
              </Button>
              <Button variant="primary" size="sm" type="submit" disabled={salvando}>
                {salvando ? 'Salvando...' : (modoEdicao ? 'Atualizar' : 'Salvar')}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>

    <ModalCadastroExterno
      show={showCadastroExterno}
      onHide={() => { setShowCadastroExterno(false); }}
      onSucesso={recarregarDisponiveis}
      documentosConhecidos={documentosConhecidos}
    />
    </>
  );
}
